/**
 * usePriorityOps — React Query hooks for priority workflow endpoints.
 *
 * Wraps /api/priority-ops/* endpoints for admin priority management.
 * Only called inside PriorityGate/PriorityListGate components.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// ─── Query Key Factory ─────────────────────────────────────────────────────

export const priorityOpsKeys = {
  all: ['priority-ops'] as const,
  list: (associationId: string, params?: { search?: string; limit?: number; offset?: number }) =>
    params
      ? [...priorityOpsKeys.all, 'list', associationId, params] as const
      : [...priorityOpsKeys.all, 'list', associationId] as const,
  member: (associationId: string, userProfileId: string) =>
    [...priorityOpsKeys.all, 'member', associationId, userProfileId] as const,
  rankBand: (associationId: string, userProfileId: string) =>
    [...priorityOpsKeys.all, 'rank-band', associationId, userProfileId] as const,
};

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PriorityListItem {
  priority_position: number;
  card_number: string;
  user_profile_id: string;
  display_first_name: string;
  display_last_name: string;
  /** Legal names — display-only fallback when display names are unset. */
  first_name: string;
  last_name: string;
  /** Current ferris-wheel group (1..4) from the last draw, or null if undrawn. */
  current_group: number | null;
  last_rodeo_date: string | null;
}

interface PriorityListResponse {
  items: PriorityListItem[];
  total: number;
  success: boolean;
}

interface RankBandResponse {
  band_label: string;
  band_index: number;
  success: boolean;
}

export interface MemberPriorityResponse {
  id: string;
  association_id: string;
  user_profile_id: string;
  card_number: string;
  priority_position: number;
  joined_at: string;
  total_members: number;
  success: boolean;
}

interface SetMemberPriorityResponse {
  priority_position: number;
  total_members: number;
  success: boolean;
}

interface RotationResult {
  association_id: string;
  total_members: number;
  entered_count: number;
  advance_amount: number;
  updated_count: number;
  success: boolean;
}

export interface BulkReorderDiffEntry {
  user_profile_id: string;
  card_number: string;
  display_first_name: string;
  display_last_name: string;
  from_position: number;
  to_position: number;
}

export interface BulkReorderResult {
  applied: boolean;
  total_members: number;
  changed_count: number;
  diff: BulkReorderDiffEntry[];
  success: boolean;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch priority list for an association (admin/delegate only).
 */
export function usePriorityList(
  associationId: string,
  params?: { search?: string; limit?: number; offset?: number }
) {
  return useQuery<PriorityListResponse>({
    queryKey: priorityOpsKeys.list(associationId, params),
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        association_id: associationId,
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.limit ? { limit: String(params.limit) } : {}),
        ...(params?.offset ? { offset: String(params.offset) } : {}),
      });

      const response = await httpClient.get(`/priority-ops/list?${queryParams}`);
      return response as PriorityListResponse;
    },
    enabled: !!associationId,
  });
}

/**
 * Fetch rank band for a member (authenticated, non-admin safe).
 * Shows approximate rank ("top 20%"), NOT exact position.
 */
export function useRankBand(associationId: string, userProfileId: string) {
  return useQuery<RankBandResponse>({
    queryKey: priorityOpsKeys.rankBand(associationId, userProfileId),
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        association_id: associationId,
        user_profile_id: userProfileId,
      });

      const response = await httpClient.get(`/priority-ops/rank-band?${queryParams}`);
      return response as RankBandResponse;
    },
    enabled: !!associationId && !!userProfileId,
  });
}

/**
 * Fetch a single member's exact draw-priority position + list size
 * (admin/delegate only). Drives the admin-only "draw score" field.
 *
 * `enabled` is caller-controlled so the request only fires when an admin
 * actually expands the field — the priority list should never leak to
 * contestants.
 */
export function useMemberPriority(
  associationId: string,
  userProfileId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<MemberPriorityResponse>({
    queryKey: priorityOpsKeys.member(associationId, userProfileId),
    queryFn: async () => {
      const response = await httpClient.get(
        `/priority-ops/member/${associationId}/${userProfileId}`,
      );
      return response as MemberPriorityResponse;
    },
    enabled:
      (options?.enabled ?? true) && !!associationId && !!userProfileId,
    retry: false,
  });
}

/**
 * Admin override of a member's exact draw-priority position. The backend
 * re-indexes the whole 1..N ferris-wheel list atomically.
 */
export function useSetMemberPriority() {
  const queryClient = useQueryClient();

  return useMutation<
    SetMemberPriorityResponse,
    Error,
    { associationId: string; userProfileId: string; priority_position: number }
  >({
    mutationFn: async ({ associationId, userProfileId, priority_position }) => {
      const response = await httpClient.put(
        `/priority-ops/member/${associationId}/${userProfileId}`,
        { priority_position },
      );
      return response as SetMemberPriorityResponse;
    },
    onSuccess: (_data, variables) => {
      // The reorder shifts other members too — drop the member entry and
      // the whole association list so positions re-fetch fresh.
      queryClient.invalidateQueries({
        queryKey: priorityOpsKeys.member(
          variables.associationId,
          variables.userProfileId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: priorityOpsKeys.list(variables.associationId),
      });
    },
  });
}

/**
 * Trigger manual ferris-wheel rotation (admin only).
 */
export function useRotatePriority() {
  const queryClient = useQueryClient();

  return useMutation<RotationResult, Error, { rodeo_id: string; association_id: string }>({
    mutationFn: async (data) => {
      const response = await httpClient.post('/priority-ops/rotate', data);
      return response as RotationResult;
    },
    onSuccess: (_data, variables) => {
      // Invalidate the list for this association
      queryClient.invalidateQueries({
        queryKey: priorityOpsKeys.list(variables.association_id),
      });
    },
  });
}

/**
 * Bulk reorder the entire priority list for an association.
 *
 * Sends the final ordered array of user_profile_ids. Pass `dryRun: true` to
 * have the server return the from→to diff without writing — used to drive
 * the preview-before-save confirmation modal on the admin page.
 *
 * On a successful real apply, invalidates the list for the association so
 * the table re-fetches and the next draft starts from server state.
 */
export function useBulkReorderPriority() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkReorderResult,
    Error,
    { associationId: string; orderedUserProfileIds: string[]; dryRun?: boolean }
  >({
    mutationFn: async ({ associationId, orderedUserProfileIds, dryRun }) => {
      const response = await httpClient.put(
        `/priority-ops/bulk-reorder/${associationId}`,
        {
          ordered_user_profile_ids: orderedUserProfileIds,
          dry_run: dryRun === true,
        },
      );
      return response as BulkReorderResult;
    },
    onSuccess: (data, variables) => {
      // Only invalidate when we actually wrote to the DB. Dry-runs are pure
      // reads and re-fetching the list would race the user's in-progress
      // draft and overwrite their pending positions in the table.
      if (data.applied) {
        queryClient.invalidateQueries({
          queryKey: priorityOpsKeys.list(variables.associationId),
        });
      }
    },
  });
}

/**
 * Seed initial priority ordering (admin only).
 */
export function useSeedPriority() {
  const queryClient = useQueryClient();

  return useMutation<{ seeded_count: number; success: boolean }, Error, { association_id: string }>({
    mutationFn: async (data) => {
      const response = await httpClient.post('/priority-ops/seed', data);
      return response as { seeded_count: number; success: boolean };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: priorityOpsKeys.list(variables.association_id),
      });
    },
  });
}
